import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { isAdminApiEnabled } from "../../config/adminApiFeature";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  bulkPatchServiceRequests,
  bulkRemoveServiceRequests,
  fetchServiceRequestsList,
} from "../../store/slices/serviceRequestsSlice";
import {
  ADMIN_TASK_STATUS_OPTIONS,
  adminStatusBadgeClass,
  adminStatusHint,
  adminStatusLabel,
  canonicalAdminStatus,
} from "./adminStatusUi";
import {
  AdminInboxBulkBar,
  AdminInboxSelectAllHeader,
  useAdminInboxSelection,
} from "./AdminInboxBulkBar";
import { highlightSearchText } from "./adminSearchHighlight";
import { ServiceRequestClientEmailColumn } from "./adminRequestClientEmailColumn";
import { RequestStatusIdleCell } from "./adminRequestIdle";

function requestAiPill(ai) {
  const s = ai?.status || "none";
  if (s === "pending") {
    return (
      <span className="admin-dash__ai-pill admin-dash__ai-pill--pending" title="Analysis in progress">
        AI
      </span>
    );
  }
  if (s === "ok") {
    return (
      <span className="admin-dash__ai-pill admin-dash__ai-pill--ok" title="Analysis ready">
        AI
      </span>
    );
  }
  if (s === "error") {
    return (
      <span className="admin-dash__ai-pill admin-dash__ai-pill--err" title="Analysis failed">
        AI
      </span>
    );
  }
  return (
    <span className="admin-dash__ai-pill admin-dash__ai-pill--none" title="No analysis yet">
      —
    </span>
  );
}

function buildRequestListParams(debouncedQ, status, sort) {
  const sortParam = sort === "newest" ? "createdAt:desc" : "createdAt:asc";
  const params = { sort: sortParam };
  if (debouncedQ.trim()) params.q = debouncedQ.trim();
  if (status !== "all") params.status = status;
  return params;
}

const SKELETON_ROWS = 6;

export default function AdminRequestsPage() {
  const apiOn = isAdminApiEnabled();
  const dispatch = useAppDispatch();
  const apiItems = useAppSelector((s) => s.serviceRequests.list.items);
  const listStatus = useAppSelector((s) => s.serviceRequests.listStatus);
  const rows = useMemo(() => (apiOn ? apiItems : []), [apiOn, apiItems]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [service, setService] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("newest");
  const debouncedQ = useDebouncedValue(q, 400);
  const [, setIdleMinuteBump] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setIdleMinuteBump((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!apiOn) return;
    dispatch(fetchServiceRequestsList(buildRequestListParams(debouncedQ, status, sort)));
  }, [apiOn, dispatch, debouncedQ, status, sort]);

  const serviceOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.serviceTitle) set.add(r.serviceTitle);
    });
    return [...set].sort();
  }, [rows]);

  const hasActiveFilters = Boolean(
    q.trim() || type !== "all" || service !== "all" || status !== "all"
  );

  const filtered = useMemo(() => {
    let list = [...rows];
    const qq = q.trim().toLowerCase();
    if (qq) {
      list = list.filter((r) => {
        const blob = `${r.name} ${r.email} ${r.companyName} ${r.requirements} ${r.description} ${r.serviceTitle}`.toLowerCase();
        return blob.includes(qq);
      });
    }
    if (type !== "all") list = list.filter((r) => r.requesterType === type);
    if (service !== "all") list = list.filter((r) => r.serviceTitle === service);
    if (status !== "all") {
      list = list.filter((r) => canonicalAdminStatus(r.status) === status);
    }
    list.sort((a, b) => {
      const ta = new Date(a.createdAt).getTime();
      const tb = new Date(b.createdAt).getTime();
      return sort === "newest" ? tb - ta : ta - tb;
    });
    return list;
  }, [rows, q, type, service, status, sort]);

  const renderSkeletonRows = useCallback(
    () =>
      Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <tr key={`sk-${i}`} className="admin-dash__table-skeleton-row" aria-hidden>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 22 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell admin-dash__table-skeleton-cell--tiny" />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell admin-dash__table-skeleton-cell--short" />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 88 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 120 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 100 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 100 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 140 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 72 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 100 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 88 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 48 }} />
          </td>
        </tr>
      )),
    []
  );

  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("new");
  const selection = useAdminInboxSelection(filtered);

  const tableBusy =
    apiOn && listStatus === "loading" && rows.length === 0;
  const showBulkChrome = apiOn && !tableBusy && filtered.length > 0;

  const clearSelection = selection.clear;
  const runBulk = useCallback(
    async (fn) => {
      setBulkBusy(true);
      try {
        await dispatch(fn).unwrap();
        clearSelection();
      } catch {
        /* toasts from thunk */
      } finally {
        setBulkBusy(false);
      }
    },
    [dispatch, clearSelection]
  );

  const onBulkMarkRead = useCallback(() => {
    const ids = selection.selectedIds;
    if (!ids.length) return;
    runBulk(
      bulkPatchServiceRequests({
        ids,
        body: { seenAt: new Date().toISOString() },
      })
    );
  }, [runBulk, selection]);

  const onBulkApplyStatus = useCallback(() => {
    const ids = selection.selectedIds;
    if (!ids.length) return;
    runBulk(
      bulkPatchServiceRequests({
        ids,
        body: { status: bulkStatus },
      })
    );
  }, [bulkStatus, runBulk, selection]);

  const onBulkDelete = useCallback(() => {
    const ids = selection.selectedIds;
    if (!ids.length) return;
    runBulk(bulkRemoveServiceRequests(ids));
  }, [runBulk, selection]);

  const emptyMessage =
    !apiOn
      ? {
          title: "Admin API is off",
          sub: "Enable the admin API for this build and sign in to load service requests from your backend.",
        }
      : rows.length === 0 && !hasActiveFilters
        ? {
            title: "No service requests yet",
            sub: "New submissions will appear here after they are stored by your backend.",
          }
        : {
            title: "No matching requests",
            sub: "Try clearing filters or broadening your search.",
          };

  return (
    <div className="admin-dash__page">
      <header className="admin-dash__page-head">
        <p className="admin-dash__page-eyebrow">Inbox</p>
        <h1 className="admin-dash__h1">Service requests</h1>
        <p className="admin-dash__lede">
          Filter by client pipeline stage (new through confirmed or rejected) and service, then search
          when you need a specific person or phrase. <strong>Idle</strong> is time without a move in{" "}
          <em>New</em> or <em>Waiting response</em>: green under 10 hours, orange from 10 hours up to
          one day, red after more than a day (hover a pill for the exact clock).
        </p>
      </header>

      <div className="admin-dash__filters-top" role="search" aria-label="Filter requests">
        <div className="admin-dash__filter-group admin-dash__filter-group--grow">
          <span className="admin-dash__filter-group-label">Search</span>
          <input
            className="admin-dash__input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, email, requirements…"
          />
        </div>
        <div className="admin-dash__filter-group">
          <span className="admin-dash__filter-group-label">Type</span>
          <select
            className="admin-dash__input"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="all">All</option>
            <option value="company">Company</option>
            <option value="individual">Individual</option>
          </select>
        </div>
        <div className="admin-dash__filter-group">
          <span className="admin-dash__filter-group-label">Service</span>
          <select
            className="admin-dash__input"
            value={service}
            onChange={(e) => setService(e.target.value)}
          >
            <option value="all">All</option>
            {serviceOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-dash__filter-group">
          <span className="admin-dash__filter-group-label">Status</span>
          <select
            className="admin-dash__input"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="all">All</option>
            {ADMIN_TASK_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-dash__filter-group">
          <span className="admin-dash__filter-group-label">Sort</span>
          <select
            className="admin-dash__input"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>
        <p className="admin-dash__filters-meta">
          Showing {filtered.length} of {rows.length} requests
        </p>
      </div>

      {showBulkChrome ? (
        <AdminInboxBulkBar
          selectedCount={selection.selectedCount}
          onClear={selection.clear}
          onMarkRead={onBulkMarkRead}
          bulkStatus={bulkStatus}
          onBulkStatusChange={setBulkStatus}
          onApplyStatus={onBulkApplyStatus}
          onRequestDelete={onBulkDelete}
          busy={bulkBusy}
          noun="requests"
        />
      ) : null}

      <div className="admin-dash__table-panel">
        <div className="admin-dash__table-wrap admin-dash__table-wrap--scroll">
          <table className="admin-dash__table">
            <caption className="admin-dash__sr-only">
              Service requests list. Columns include pipeline status, client email stage and offer line,
              idle window, and AI analysis. Select rows when bulk actions are available.
            </caption>
            <thead>
              <tr>
                {showBulkChrome ? (
                  <AdminInboxSelectAllHeader
                    allSelected={selection.allSelected}
                    someSelected={selection.someSelected}
                    onToggleAll={selection.toggleAllVisible}
                    disabled={bulkBusy}
                  />
                ) : (
                  <th scope="col" className="admin-dash__th-select" aria-hidden />
                )}
                <th scope="col" className="admin-dash__th-dot-col">
                  <span className="admin-dash__sr-only">Unread</span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Submitted</span>
                    <span className="admin-dash__th-sub">Date &amp; time</span>
                  </span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Type</span>
                    <span className="admin-dash__th-sub">Requester</span>
                  </span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Service</span>
                  </span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Name</span>
                  </span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Email</span>
                  </span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Status</span>
                    <span className="admin-dash__th-sub">Pipeline</span>
                  </span>
                </th>
                <th
                  scope="col"
                  className="admin-dash__th-client-email"
                  title="Row A — stage in the client thread (one of: Not sent, Sent, Reply). Row B — AI read on the latest inbound reply (or —). API: emailStage or emailMessages (see frontend-admin-email-api.md)."
                >
                  <div className="admin-dash__th-email-head">
                    <div className="admin-dash__th-pair">
                      <span className="admin-dash__th-line">Stage</span>
                      <span className="admin-dash__th-sub">
                        Not sent · Sent · Reply
                      </span>
                    </div>
                    <div className="admin-dash__th-pair">
                      <span className="admin-dash__th-line">Offer</span>
                      <span className="admin-dash__th-sub">AI on latest inbound</span>
                    </div>
                  </div>
                </th>
                <th
                  scope="col"
                  className="admin-dash__th-idle"
                  title='Time in "New" (since submitted) or "Waiting response" (since last update). Green: under 10 h. Orange: 10 h to 24 h. Red: over 24 h.'
                >
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Idle</span>
                    <span className="admin-dash__th-sub">Response gap</span>
                  </span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">AI</span>
                    <span className="admin-dash__th-sub">Request</span>
                  </span>
                </th>
                <th scope="col" className="admin-dash__th-actions">
                  <span className="admin-dash__sr-only">Open detail</span>
                </th>
              </tr>
            </thead>
          <tbody>
            {apiOn && listStatus === "loading" && rows.length === 0 ? (
              renderSkeletonRows()
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={12} className="admin-dash__empty admin-dash__empty-state">
                  <span className="admin-dash__empty-state-title">{emptyMessage.title}</span>
                  <span className="admin-dash__empty-state-sub">{emptyMessage.sub}</span>
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id} className={!r.seenAt ? "admin-dash__tr-unread" : undefined}>
                  {showBulkChrome ? (
                    <td className="admin-dash__th-select">
                      <input
                        type="checkbox"
                        className="admin-dash__checkbox"
                        checked={selection.selected.has(r.id)}
                        onChange={() => selection.toggle(r.id)}
                        disabled={bulkBusy}
                        aria-label={`Select request ${r.name || r.email || r.id}`}
                      />
                    </td>
                  ) : (
                    <td className="admin-dash__th-select" aria-hidden />
                  )}
                  <td className="admin-dash__td-dot">
                    {!r.seenAt ? (
                      <span className="admin-dash__unread-dot" title="Unread" />
                    ) : null}
                  </td>
                  <td className="admin-dash__td-muted">
                    {new Date(r.createdAt).toLocaleString()}
                  </td>
                  <td>
                    <span className="admin-dash__badge-type">
                      {r.requesterType === "individual" ? "Individual" : "Company"}
                    </span>
                  </td>
                  <td>{highlightSearchText(r.serviceTitle || "—", q)}</td>
                  <td>{highlightSearchText(r.name || "—", q)}</td>
                  <td>{highlightSearchText(r.email || "—", q)}</td>
                  <td>
                    <div className="admin-dash__td-badges">
                      <span
                        className={adminStatusBadgeClass(r.status)}
                        title={adminStatusHint(r.status)}
                      >
                        {adminStatusLabel(r.status)}
                      </span>
                      {(r.replies || []).length > 0 ? (
                        <span
                          className="admin-dash__badge-replies"
                          title="Internal replies"
                        >
                          {(r.replies || []).length} repl
                          {(r.replies || []).length === 1 ? "y" : "ies"}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="admin-dash__td-client-email">
                    <ServiceRequestClientEmailColumn row={r} />
                  </td>
                  <td className="admin-dash__td-idle">
                    <RequestStatusIdleCell row={r} />
                  </td>
                  <td>{requestAiPill(r.aiAnalysis)}</td>
                  <td className="admin-dash__td-actions">
                    <Link className="admin-dash__link" to={`/admin/dashboard/requests/${r.id}`}>
                      Open
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
